import charts from './util/charts.js';
import 'styles/minisites/devops-report/charts.scss';

export const chartConfig = {
	/* Work out appropriate chart colors for given section */
	getColors: (section, data) => {
		if ((!data.type || data.type === 'bar') && !data.labels) {
			return ['#FFE4A0'];
		}

		if (data.config === 'impact') {
			return [
				'#232E54',
				'#FFB945',
			];
		}

		return [
			'#FFB945',
			'#232E54',
			'#1275D9',
			'#FFD35C',
			'#8ED1EE',
			'#FFE6A7',
			'#F8CEB7',
			'#C6C6C6',
		];
	},

	configs: {
		drivers: {
			plugins: {
				tooltip: {
					external: false,
				},
				datalabels: {
					align: 'end',
					anchor: 'start',
					color: '#fff',
					offset: 8,
				},
			},
			scales: {
				y: {
					position: 'left',
					ticks: {
						crossAlign: 'far',
					},
				},
				x: {
					reverse: true,
				},
			},
		},
		barriers: {
			plugins: {
				tooltip: {
					external: false,
				},
			},
			scales: {
				y: {
					position: 'right',
					ticks: {
						crossAlign: 'far',
					},
				},
			},
		},
		roi: {
			indexAxis: 'x',
			plugins: {
				htmlLegend: false,
				datalabels: false,
				tooltip: {
					external: false,
				},
			},
			scales: {
				x: {
					ticks: {
						display: true,
						crossAlign: 'center',
						padding: 16,
						maxRotation: 0,
						autoSkip: false,
						font: {
							size: 12,
						},
						callback(value, index) {
							const skip = window.innerWidth < 575 ? 4 : 2;
							return (index + 1) % skip === 0 ? `${this.getLabelForValue(value)} months` : '';
						},
					},
				},
				y: {
					max(scale) {
						/* Get full dataset */
						const originalData = JSON.parse(scale.chart?.canvas?.dataset?.data);
						/* Get max ROI at 12 months */
						const maxRoi = originalData[originalData.length - 1].value * 12;
						/* Round up to nearest 100k */
						return Math.ceil(maxRoi / 100000) * 100000;
					},
					ticks: {
						font: {
							size: 12,
						},
						callback(value) {
							/* Format as dollars */
							const numericalValue = Number.parseFloat(value);
							if (Number.isNaN(numericalValue)) {
								return value;
							}

							return numericalValue.toLocaleString('en-US', {
								style: 'currency',
								currency: 'USD',
								minimumFractionDigits: 0,
								maximumFractionDigits: 0,
							});
						},
					},
				},
			},
		},
		impact: {
			categoryPercentage: 0.7,
			indexAxis: 'x',
			scales: {
				x: {
					ticks: {
						display: false,
					},
				},
				y: {
					ticks: {
						display: false,
					},
				},
			},
			plugins: {
				datalabels: {
					align: 'start',
					offset: 8,
				},
			},
		},
		/* Comparison bars show each bar's own value (with an optional unit such
		   as a percentage sign) rather than its share of the dataset total */
		comparison: {
			plugins: {
				datalabels: {
					formatter: (value, context) => `${value}${context.chart.canvas.dataset.unit || ''}`,
				},
				tooltip: {
					callbacks: {
						label: context => `${context.raw}${context.chart.canvas.dataset.unit || ''}`,
					},
				},
			},
		},
	},

	data: {
		roi(processedData, originalData, chartContainer) {
			const teamSize = chartContainer.querySelector('#roi-team-size')?.value || 0;
			const monthlyRoi = originalData[teamSize]?.value || originalData[originalData.length - 1].value;

			/* Generate ROI data for a year */
			const roiData = [];
			const roiLabels = [];
			for (let i = 1; i <= 12; i++) {
				roiData.push(monthlyRoi * i);
				roiLabels.push(i);
			}

			return {
				datasets: [
					{
						data: roiData,
						total: roiData,
						backgroundColor: processedData.datasets[0].backgroundColor,
						label: 'ROI over time',
					},
				],
				labels: roiLabels,
			};
		},
		/* Replace the share-of-total values with each bar's actual value so the
		   bars and labels reflect the real figure (a percentage or a count) */
		comparison(processedData, originalData) {
			return {
				labels: processedData.labels,
				datasets: processedData.datasets.map(dataset => ({
					...dataset,
					data: originalData.map(row => row.value),
					isPercentage: false,
				})),
			};
		},
		impact(processedData, originalData, chartContainer) {
			/* Calculate percentage difference */
			const difference = (processedData.datasets[1].total[0] / processedData.datasets[0].total[0]) - 1;
			const formatted = new Intl.NumberFormat('en-US', { style: 'percent' }).format(
				difference,
			);

			chartContainer.querySelector('figcaption strong').textContent = formatted;

			const data = {
				datasets: processedData.datasets.map((label, index) => ({
					label: processedData.datasets[index].label,
					data: [processedData.datasets[index].total[0] / 100],
					total: [1],
					isPercentage: true,
					backgroundColor: processedData.datasets[index].backgroundColor,
				})),
				labels: processedData.labels,
			};
			return data;
		},
	},
};

document.addEventListener('DOMContentLoaded', () => {
	charts.setUpCharts(chartConfig);
});
